import { useState } from 'react';
import { Badge, CloseButton, Group, Select, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useContractsLandList } from '../queries/contracts-land';
import { useLeasesList } from '../queries/leases';
import { useWellsList } from '../queries/wells';
import {
  useAddXref,
  useRemoveXref,
  useXrefs,
  XREF_RELATED_TYPES,
  type XrefEntity,
} from '../queries/xrefs';
import { ApiError } from './api-error';
import type { ContractLand, Lease, Well, XrefTarget } from '../types/api';

interface Props {
  entity: XrefEntity;
  /** The saved record's id, or null for a brand-new/unsaved record. */
  id: string | null;
}

const ENTITY_LABEL: Record<XrefEntity, string> = {
  lease: 'lease',
  contract: 'contract',
  well: 'well',
};

const ENTITY_LABEL_TITLE: Record<XrefEntity, string> = {
  lease: 'Lease',
  contract: 'Contract',
  well: 'Well',
};

const ENTITY_LABEL_PLURAL: Record<XrefEntity, string> = {
  lease: 'Leases',
  contract: 'Contracts',
  well: 'Wells',
};

/** Response field each related type is grouped under, mirroring
 * api/src/lib/xref-router.ts's `pluralKey`. */
function pluralKeyFor(type: XrefEntity): 'leases' | 'contracts' | 'wells' {
  switch (type) {
    case 'lease':
      return 'leases';
    case 'contract':
      return 'contracts';
    case 'well':
      return 'wells';
  }
}

// Client-side label builders mirroring the API's labelSql (api/src/routes/land-xrefs.ts)
// so the "+ Link a X" combobox options read the same as the resulting chips.
function labelForLease(l: Lease): string {
  return l.prospect ? `${l.lease_number} · ${l.prospect}` : l.lease_number;
}
function labelForContract(c: ContractLand): string {
  return c.contract_title ? `${c.contract_number} · ${c.contract_title}` : c.contract_number;
}
function labelForWell(w: Well): string {
  return w.property_name ? `${w.property_number} · ${w.property_name}` : w.property_number;
}

/**
 * Shared "Cross-references" section for the lease/contract/well drawers
 * (spec docs/superpowers/specs/2026-08-16-land-data-xrefs-design.md §4).
 * Renders one "Linked <RelatedType>" group per related type with removable
 * chips, plus a searchable "+ Link a <RelatedType>" combobox per group.
 *
 * When `id` is null (a brand-new, unsaved record) there is nothing to link
 * yet — the section renders a muted note instead of the live controls.
 */
export function CrossReferenceSection({ entity, id }: Props) {
  const xrefsQ = useXrefs(entity, id);
  const addM = useAddXref(entity, id);
  const removeM = useRemoveXref(entity, id);

  // Always call all three list queries (Rules of Hooks) — only the ones for
  // this entity's two related types are actually consumed below. These are
  // the same project-scoped list queries the list pages use, so they share
  // the query cache rather than issuing a redundant fetch.
  const leasesQ = useLeasesList();
  const contractsQ = useContractsLandList();
  const wellsQ = useWellsList();

  const candidatesByType: Record<XrefEntity, XrefTarget[]> = {
    lease: (leasesQ.data ?? []).map((l) => ({ id: l.id, label: labelForLease(l) })),
    contract: (contractsQ.data ?? []).map((c) => ({ id: c.id, label: labelForContract(c) })),
    well: (wellsQ.data ?? []).map((w) => ({ id: w.id, label: labelForWell(w) })),
  };

  if (id == null) {
    return (
      <Stack gap="xs">
        <Title order={5}>Cross-references</Title>
        <Text size="sm" c="dimmed">
          {`Save the ${ENTITY_LABEL[entity]} first to add cross-references.`}
        </Text>
      </Stack>
    );
  }

  const grouped = xrefsQ.data as Partial<Record<'leases' | 'contracts' | 'wells', XrefTarget[]>> | undefined;

  async function handleAdd(type: XrefEntity, targetId: string) {
    try {
      await addM.mutateAsync({ type, target_id: targetId });
    } catch (err) {
      notifications.show({
        title: 'Link failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  async function handleRemove(type: XrefEntity, target: XrefTarget) {
    try {
      await removeM.mutateAsync({ type, target_id: target.id });
    } catch (err) {
      notifications.show({
        title: 'Remove failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  return (
    <Stack gap="md">
      <Title order={5}>Cross-references</Title>
      {XREF_RELATED_TYPES[entity].map((type) => {
        const linked = grouped?.[pluralKeyFor(type)] ?? [];
        const linkedIds = new Set(linked.map((t) => t.id));
        const options = candidatesByType[type]
          .filter((t) => t.id !== id && !linkedIds.has(t.id))
          .map((t) => ({ value: t.id, label: t.label }));

        return (
          <Stack key={type} gap={4}>
            <Text size="sm" fw={500}>
              {`Linked ${ENTITY_LABEL_PLURAL[type]}`}
            </Text>
            <Group gap="xs">
              {linked.length === 0 && (
                <Text size="xs" c="dimmed">
                  None linked
                </Text>
              )}
              {linked.map((t) => (
                <Badge
                  key={t.id}
                  variant="light"
                  color="indigo"
                  rightSection={
                    <CloseButton
                      size="xs"
                      variant="transparent"
                      aria-label={`Remove ${t.label}`}
                      onClick={() => void handleRemove(type, t)}
                    />
                  }
                >
                  {t.label}
                </Badge>
              ))}
            </Group>
            <XrefLinkPicker
              entityLabel={ENTITY_LABEL_TITLE[type]}
              options={options}
              onSelect={(targetId) => void handleAdd(type, targetId)}
            />
          </Stack>
        );
      })}
    </Stack>
  );
}

function XrefLinkPicker({
  entityLabel,
  options,
  onSelect,
}: {
  entityLabel: string;
  options: { value: string; label: string }[];
  onSelect: (targetId: string) => void;
}) {
  const [value, setValue] = useState<string | null>(null);
  const label = `+ Link a ${entityLabel}`;
  return (
    <Select
      label={label}
      placeholder="Search…"
      data={options}
      value={value}
      searchable
      clearable
      nothingFoundMessage="No candidates"
      onChange={(v) => {
        setValue(null);
        if (v) onSelect(v);
      }}
    />
  );
}
